import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { useListEntitiesQuery, useListConnectionsQuery } from '../redux/api/entityApi';
import { setEntities, setConnections, setLoadingEntities, setLoadingConnections } from '../redux/slices/siteSlice';

/**
 * Hook to fetch and manage entities and connections for the current site
 * Automatically fetches when site changes and updates Redux state
 */
export const useSiteEntities = () => {
  const dispatch = useAppDispatch();
  const currentSite = useAppSelector((state) => state.site.currentSite);
  
  // Get portfolio_id and site_id from current site
  const portfolioId = currentSite?.portfolio_id;
  const siteId = currentSite?.site_id;

  // Fetch entities
  const {
    data: entitiesData,
    isLoading: isLoadingEntities,
    error: entitiesError,
  } = useListEntitiesQuery(
    { portfolio_id: portfolioId!, site_id: siteId! },
    {
      skip: !portfolioId || !siteId,
      refetchOnMountOrArgChange: true,
    }
  );

  // Fetch connections
  const {
    data: connectionsData,
    isLoading: isLoadingConnections,
    error: connectionsError,
  } = useListConnectionsQuery(
    { portfolio_id: portfolioId!, site_id: siteId! },
    {
      skip: !portfolioId || !siteId,
      refetchOnMountOrArgChange: true,
    }
  );

  // Update Redux state when data changes
  useEffect(() => {
    if (entitiesData) {
      dispatch(setEntities(entitiesData));
    }
  }, [entitiesData, dispatch]);

  useEffect(() => {
    if (connectionsData) {
      dispatch(setConnections(connectionsData));
    }
  }, [connectionsData, dispatch]);

  useEffect(() => {
    dispatch(setLoadingEntities(isLoadingEntities));
  }, [isLoadingEntities, dispatch]);

  useEffect(() => {
    dispatch(setLoadingConnections(isLoadingConnections));
  }, [isLoadingConnections, dispatch]);

  return {
    entities: entitiesData || [],
    connections: connectionsData || [],
    isLoadingEntities,
    isLoadingConnections,
    entitiesError,
    connectionsError,
  };
};

